<script setup>
import {computed, onMounted, ref, watch} from 'vue'
import moment from 'moment'
import api from './../api'
import Loader from './Loader'
import Datepicker from './../../plugins/vuejs-datepicker/components/Datepicker.vue'
import useStore from "./../store"
import {storeToRefs} from "pinia";
import FieldError from "./FieldError.vue";
import AlertError from "./AlertError.vue";
import ErrorWrapper from "./ErrorWrapper.vue";
import { oneDate, oneDateOnly, isWalkInOnly } from "../helpers/attractions";
import { availabilityRequestDate, parseNextAvailabilityDate } from "../helpers/availability";
import { disabledDatesForProduct } from "../../helpers/disabled-dates";

// Props
const props = defineProps({
  attraction: {
    type: [Object, Boolean],
    default: () => ({}),
  },
  errors: {
    type: Array,
    default: () => [],
  },
  // value: {
  //   type: [Array, String, Boolean],
  //   default: '',
  // },
  test: {
    type: Boolean,
    default: false,
  },
  submitted: {
    type: Boolean,
    default: false,
  },
  totalPeople: {
    type: Number,
    default: 0,
  },
  productCode: {
    type: [String, Boolean, Number],
    default: null,
  }
})

// Emits
const emit = defineEmits([
  'input',
  // 'update:model-value',
  'remove',
  'selectExtraAttraction'
])

const model = defineModel({
  type: [Array, String, Boolean],
  default: '',
})

const prices = defineModel('prices', {
  type: [Array],
  default: [],
})

const availability = defineModel('availability', {
  type: [Object],
  default: [],
})

// Reactive state
const disabledDates = ref({
  to: moment().subtract(1, 'day').toDate(),
})

const dateField = ref(null)
const dates = ref([])
const times = ref([])
const loading = ref(false)
const loaded = ref(false)
const dateStart = ref(moment().format('YYYY-MM-DD'))
const date = ref(moment().format('YYYY-MM-DD'))
const time = ref('')
const allTimes = ref(false)
const nextDate = ref(null)
const initial = ref(true)

const store = useStore();
const { attractions, items } = storeToRefs(store);

// Computed properties
// const seatsNeeded = computed(() => props.totalPeople < 1 ? 1 : props.totalPeople)
const seatsNeeded = 1

const hasAttraction = computed(() => {
  return !props.attraction?.id ?? false;
})

const hasPrevious = computed(() => {
  return moment(dateStart.value).isAfter(moment(), 'day')
})

const currentAttraction = computed(() => {
  return attractions.value.find(attraction => Number(attraction.id) === Number(props.attraction.id))
})

const minimumNoticeMinutes = computed(() => {
  const configuredAttraction = currentAttraction.value ?? props.attraction
  const attractionNotice = Number(props.attraction?.cutoff_minutes ?? 0)
  const optionNotice = Number(
    configuredAttraction?.rezdy_product_options?.find(option => option.product === props.productCode)?.minimumNoticeMinutes ?? 0
  )

  return Math.max(
    Number.isFinite(attractionNotice) ? attractionNotice : 0,
    Number.isFinite(optionNotice) ? optionNotice : 0
  )
})

const availableTimes = computed(() => {
  if (times.value.length < 1) {
    return []
  }
  return allTimes.value ? times.value : times.value.slice(0, 11)
})

const currentDisabledDates = computed(() => disabledDatesForProduct(
  props.attraction,
  props.productCode
))

const refreshDisabledDates = () => {
  const dateValues = currentDisabledDates.value

  disabledDates.value = {
    to: moment().subtract(1, 'day').toDate(),
    ...(dateValues.length > 0
      ? { dates: dateValues.map(date => moment(date, 'YYYY-MM-DD').toDate()) }
      : {})
  }
}

const buildDates = async () => {
  let today = moment(dateStart.value)
  let future = moment(dateStart.value).add(6, 'd')
  let days = []
  let day = today.clone()

  if (minimumNoticeMinutes.value && today.isSame(moment(), 'day')){
    day = day.add(minimumNoticeMinutes.value, 'minutes')
  }

  let disabled_dates = currentDisabledDates.value

  while (day <= future) {
    let label = day.format('dddd, Do MMMM YYYY')
    let value = day.format('YYYY-MM-DD')
    days.push({
      value: value,
      date: day,
      label: label,
      disabled: disabled_dates.includes(value)
    })
    day = day.clone().add(1, 'd')
  }

  dates.value = days
}

const loadTimes = async () => {
  if (date.value === '') {
    return
  }

  if (!props.attraction?.id) {
    return
  }

  if (currentDisabledDates.value.includes(moment(date.value).format('YYYY-MM-DD'))) {
    times.value = []
    allTimes.value = false
    nextDate.value = null
    loaded.value = true
    return
  }

  loading.value = true
  loaded.value = false
  allTimes.value = false
  nextDate.value = null

  // console.log('date.value args', date.value)

  let dateValue = availabilityRequestDate(date.value, minimumNoticeMinutes.value)

  try {
    // console.log('availability args', props.attraction.id, props.productCode, dateValue)

    let data = await api.availability(
        props.attraction,
        props.productCode,
        dateValue,
        true
    )
    const after = moment().add(minimumNoticeMinutes.value, 'minutes');

    if (!Array.isArray(data) && data?.next) {
      nextDate.value = parseNextAvailabilityDate(data.next)

      if (initial.value && nextDate.value) {
        initial.value = false
        await setDay(nextDate.value)
        await buildDates()

        dateValue = availabilityRequestDate(nextDate.value, minimumNoticeMinutes.value)
        data = await api.availability(
            props.attraction,
            props.productCode,
            dateValue,
            true
        )

        nextDate.value = Array.isArray(data)
          ? null
          : parseNextAvailabilityDate(data?.next)
      }
    }

    if (!Array.isArray(data)) {
      times.value = []
      return
    }

    times.value = data
        .filter(t => moment(t.startTimeLocal).isAfter(after))
        .map(t => ({
          ...t,
          label: moment(t.startTimeLocal).format('h:mm a'),
          hourMinutes: moment(t.startTimeLocal).format('h:mm'),
          meridiem: moment(t.startTimeLocal).format('a'),
          disabled: t.seatsAvailable < 1,
          title: t.seatsAvailable < 1
              ? `${moment(t.startTimeLocal).format('h:mm a')} (fully booked)`
              : `${moment(t.startTimeLocal).format('h:mm a')} (${t.seatsAvailable} available)`
        }))

    initial.value = false

    if (oneDateOnly(props.attraction.id) && times.value.length === 1){
      await selectTime(times.value[0]);
    }

  } catch (error) {
    console.error(error)
  } finally {
    loaded.value = true
    loading.value = false
  }
}


// Methods
const setDay = async (newDate) => {
  date.value = newDate.format('YYYY-MM-DD')
  dateStart.value = date.value
  time.value = ''
}

const changedFirstDate = async (newDate) => {
  await setDay(moment(newDate))
  await buildDates()
  await loadTimes()
}

const selectDay = async (newDate) => {
  date.value = newDate.format('YYYY-MM-DD')
  time.value = ''
  prices.value = []
  availability.value = {}
  await loadTimes()
}

const selectTime = async (newTime) => {
  time.value = newTime.startTimeLocal
  prices.value = newTime.priceOptions
  availability.value = newTime

  await emitUpdate()
}

const previous = () => {
  let newDate = moment(dateStart.value).subtract(5, 'd')
  if (newDate.isBefore(moment())) {
    newDate = moment()
  }
  dateStart.value = newDate.format('YYYY-MM-DD')
  buildDates()
}

const next = () => {
  dateStart.value = moment(dateStart.value).add(5, 'd').format('YYYY-MM-DD')
  buildDates()
}

const emitUpdate = async () => {
  emit('input', time.value)
  model.value = time.value
}

const nextAvailable = async () => {
  try {
    let dateValue = moment(date.value)
        .isSame(moment(), "day") ?
        moment()
            .add(minimumNoticeMinutes.value, 'minutes')
            .format('YYYY-MM-DD HH:mm') :
        moment(date.value)
            .add(minimumNoticeMinutes.value, 'minutes')
            .format('YYYY-MM-DD 00:00')

    const data = await api.nextAvailability(props.attraction, dateValue, props.productCode)
    nextDate.value = moment(data.startTimeLocal, 'YYYY-MM-DD HH:mm:ss')
    //await selectDay(moment(data.startTimeLocal))
  } finally {
    // buildDates()
    // loadTimes()
  }
}

const nextDateAvailable = computed(() => {
  return nextDate.value ? nextDate.value.format('dddd Do MMMM') : false;
})

// Watchers
watch(time, () => {
  if (time.value) {
    emitUpdate()
  }
})

watch(() => props.productCode, async () => {
  initial.value = true
  refreshDisabledDates()

  let dateValue = moment();
  let startAt = moment(dateValue).add(minimumNoticeMinutes.value, 'minutes');

  if (!startAt.isSame(moment(), "day")){
    dateValue = startAt.format('YYYY-MM-DD');
  }

  await changedFirstDate(dateValue)
})

// Lifecycle hooks
onMounted(async () => {
  if (model.value){
    date.value = moment(model.value).format('YYYY-MM-DD');
    dateStart.value = date.value;
    time.value = moment(model.value).format('YYYY-MM-DD HH:mm:ss');
  }

  refreshDisabledDates()
  await buildDates()
  await loadTimes()

  if (oneDateOnly(props.attraction.id)){
    model.value = time.value
    emit('input', time.value)
  }
})

const walkInOnlyDate = computed(() => {
  return isWalkInOnly(props.attraction?.id, date.value)
})

const timeDescription = computed(() => {
  return props.attraction?.time_description ?? false
})

</script>
<template>
  <div class="session">
    <Loader v-if="loading"></Loader>

    <div class="relative" v-show="!oneDateOnly(attraction.id)">
      <error-wrapper :submitted="submitted" :errors="errors">

        <div v-if="hasAttraction" class="absolute -inset-4 z-20 backdrop-blur-sm bg-light/30"></div>
        <div class="pt-4">

          <div class="flex justify-between items-center mb-2 pb-2">
            <h4 class="font-bold text-dark text-xlx">Date of visit</h4>
          </div>

          <div v-if="!loading && loaded && times.length < 1" class="mb-4">
            <AlertError>
              <span>
                Sorry, there are no tours on this date.
                <span v-if="nextDateAvailable" v-text="`Next tour ${nextDateAvailable}.`"></span>
              </span>
            </AlertError>
          </div>
          <div>

            <ul class="grid lg:grid-flow-col grid-cols-3 lg:grid-cols-none lg:auto-cols-auto gap-2 items-center dates mb-4">
              <li class="previous" v-if="false">
                <button
                    title="Previous 5 days"
                    :disabled="!hasPrevious"
                    class="rounded-full h-8 w-8 bg-dark text-light flex items-center justify-center"
                    @click.prevent="previous"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
                  </svg>
                </button>
              </li>
              <li v-for="(item, ind) in dates" :key="`date-${ind}`">
                <button
                    :value="item.value"
                    :title="item.label"
                    :disabled="item.disabled"
                    type="button"
                    class="w-full flex flex-col items-center justify-center p-2 py-3 gap-1 rounded-lg disabled:line-through disabled:opacity-30"
                    :class="{
                      today: item.value === dateStart,
                      'bg-dark text-light': date === item.value,
                      'bg-white text-dark' : date !== item.value,
                    }"
                    @click.prevent="selectDay(item.date)"
                >
                  <span
                      class="text-lg leading-none"
                      v-text="item.date.format('dd')"
                  ></span>
                  <span
                      class="text-xl font-bold leading-none mb-1"
                      v-text="item.date.format('D')"
                  ></span>
                  <span
                      class="text-lg leading-none uppercase"
                      :class="{
                        'line-through' : item.disabled
                      }"
                      v-text="item.date.format('MMM')"
                  ></span>
                </button>
              </li>
              <datepicker
                  id="date"
                  ref="dateField"
                  :value="date"
                  :disabled-dates="disabledDates"
                  :calendar-button="true"
                  calendar-class="right-0"
                  input-class="bg-light text-dark cursor-pointer border-none relative z-10 rounded-lg leading-none max-w-[180px]"
                  format="D, dd/MM/yyyy"
                  :use-button="true"
                  name="date"
                  @selected="changedFirstDate"
              >
                <template v-slot:beforeCalendarHeader>
                  <div v-if="nextDateAvailable"
                       class="text-center border-b mt-2 pb-1 border-black"
                       v-text="`Next tour ${nextDateAvailable}.`"></div>
                </template>
              </datepicker>
            </ul>
          </div>

        </div>

        <div class="py-4">
          <div v-if="times.length > 0">
            <div class="mb-4">
              <h4 class="font-bold text-xlx text-dark">Start time</h4>
              <div class="text-dark" v-if="timeDescription" v-text="timeDescription"></div>
            </div>
            <label :for="`time_${attraction.id}`"></label>
            <ul class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 times gap-2 auto-rows-fr mb-4">
              <li
                  v-for="(timeItem, ind) in availableTimes"
                  :key="`time-${ind}`"
                  :class="{}"
              >
                <button
                    :value="timeItem.startTimeLocal"
                    :disabled="timeItem.seatsAvailable < seatsNeeded"
                    @click.prevent="selectTime(timeItem)"
                    class="w-full flex flex-col items-center justify-center p-2 gap-1 rounded-lg border h-full"
                    :class="{
                      'bg-dark text-light border-dark': timeItem.seatsAvailable > 0 && timeItem.startTimeLocal === time,
                      'bg-white text-dark bg-light border-light': timeItem.seatsAvailable > 0 && timeItem.startTimeLocal !== time,
                      'bg-white text-dark border-dark': timeItem.seatsAvailable < 1,
                    }"
                >
                  <span class="flex items-baseline gap-1">
                    <span v-text="`${timeItem.hourMinutes}`" class="font-bold text-2xl"></span>
                    <span v-text="`${timeItem.meridiem}`" class="text-lg uppercase"></span>
                  </span>
                  <span v-if="timeItem.seatsAvailable < 1" class="text-sm lg:text-base uppercase">{{ walkInOnlyDate ? 'Walk in only' : 'Sold Out' }}</span>
                  <span v-else-if="timeItem.seatsAvailable > 5"
                        class="text-sm lg:text-base"
                        v-text="`${timeItem.seatsAvailable} available`"></span>
                  <span
                      v-else-if="timeItem.seatsAvailable <= 5"
                      class="text-sm lg:text-base"
                      v-text="`Only ${timeItem.seatsAvailable} left`"
                  ></span>
                </button>
              </li>
              <li v-if="times.length > 11 && !allTimes">
                <button
                    class="w-full h-full flex flex-col items-center justify-center p-2 gap-1 rounded-lg"
                    @click.prevent="allTimes = true"
                >
                  <span class="flex flex-col gap-1 items-center justify-center">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" class="block h-6 w-6" xmlns="http://www.w3.org/2000/svg">
                      <rect width="24" height="24" rx="12" class="fill-dark" />
                      <path d="M12.9231 4.92308V11.0769H19.0769C19.5769 11.0769 20 11.5 20 12C20 12.5385 19.5769 12.9231 19.0769 12.9231H12.9231V19.0769C12.9231 19.6154 12.5 20 12 20C11.4615 20 11.0769 19.6154 11.0769 19.0769V12.9231H4.92308C4.38462 12.9231 4 12.5385 4 12C4 11.5 4.38462 11.0769 4.92308 11.0769H11.0769V4.92308C11.0769 4.42308 11.4615 4 12 4C12.5 4 12.9231 4.42308 12.9231 4.92308Z" fill="#F4F1EB"/>
                    </svg>
                    <span>More times</span>
                  </span>
                </button>
              </li>
            </ul>
          </div>

          <FieldError :errors="errors" />
        </div>
      </error-wrapper>
    </div>
    <div class="relative bg-light rounded-lg p-4 text-lg"
         v-if="oneDateOnly(attraction.id)" v-html="oneDate(attraction.id).message"></div>
  </div>
</template>
